import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errMessage } from "@/lib/api";
import { AuthShell, authInputCls, authLinkCls } from "@/pages/Login";

const FIELDS = [
  { k: "first_name", label: "Prénom", type: "text", auto: "given-name" },
  { k: "last_name", label: "Nom", type: "text", auto: "family-name" },
  { k: "email", label: "E-mail", type: "email", auto: "email" },
  { k: "password", label: "Mot de passe (8 caractères minimum)", type: "password", auto: "new-password" },
];
const MODES = [["join", "Rejoindre un workplace"], ["create", "Créer mon workplace"]];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("join");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", join_code: "", workplace_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
    setBusy(true);
    setError("");
    try {
      await register({ first_name: form.first_name, last_name: form.last_name, email: form.email, password: form.password,
        ...(mode === "join" ? { join_code: form.join_code } : { workplace_name: form.workplace_name }) });
      navigate("/");
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell kicker="Inscription" title="Créer un compte" subtitle="Rejoignez le workplace de votre organisation, ou créez le vôtre.">
      <div role="tablist" className="mb-4 grid grid-cols-2 gap-2">
        {MODES.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={mode === k} data-testid={`register-mode-${k}`} onClick={() => setMode(k)}
            className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${mode === k ? "border-amber-500 bg-amber-500 text-slate-950" : "border-border hover:border-amber-500/60"}`}>{l}</button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.k}>
            <Label htmlFor={f.k}>{f.label}</Label>
            <Input id={f.k} data-testid={`register-${f.k}-input`} type={f.type} autoComplete={f.auto} className={authInputCls} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} required />
          </div>
        ))}
        {mode === "join" ? (
          <div>
            <Label htmlFor="join_code">Code du workplace</Label>
            <Input id="join_code" data-testid="register-join_code-input" autoComplete="off" className={authInputCls} value={form.join_code} onChange={(e) => setForm({ ...form, join_code: e.target.value })} required />
          </div>
        ) : (
          <div>
            <Label htmlFor="workplace_name">Nom de votre workplace (entreprise, agence…)</Label>
            <Input id="workplace_name" data-testid="register-workplace_name-input" autoComplete="organization" className={authInputCls} value={form.workplace_name} onChange={(e) => setForm({ ...form, workplace_name: e.target.value })} required />
          </div>
        )}
        <p className="text-xs text-muted-foreground lg:text-slate-500">{mode === "join"
          ? "Le code du workplace vous est communiqué par votre administrateur. Les nouveaux comptes ont le rôle Opérateur."
          : "Vous serez l'administrateur de ce nouveau workplace : vous y créerez sites, équipements et utilisateurs."}</p>
        {error && <p data-testid="register-error" role="alert" className="text-sm text-red-300 lg:text-red-700">{error}</p>}
        <Button data-testid="register-submit-button" type="submit" disabled={busy} className="h-12 w-full text-base font-bold">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Créer mon compte
        </Button>
      </form>
      <p className="mt-5 text-sm text-muted-foreground lg:text-slate-600">
        Déjà inscrit ? <Link data-testid="go-login-link" to="/connexion" className={authLinkCls}>Se connecter</Link>
      </p>
    </AuthShell>
  );
}
